import { useCallback, useEffect, useRef } from 'react';
import type { FieldValues, UseFormReturn } from 'react-hook-form';
import type { FieldError } from './noon-form.js';

/**
 * Rapor formunun hataları (ortak geri bildirimi 2026-10-19): kayıt neden engellendi, ekranda açık
 * olsun. Sıra hatası karşılaştırılan zamanı taşır; değer değişince bayat hata kalkar; gönderimde
 * bölüm bölüm özet çıkar ve ilk hatalı alana gidilir.
 */

/** Hatayı forma yazar; sıra hatasının karşılaştırılan zamanı `types` içinde (`after`/`before`). */
export function applyFieldErrors<T extends FieldValues>(
  form: Pick<UseFormReturn<T>, 'setError'>,
  errors: readonly FieldError[],
): void {
  for (const error of errors) {
    const ref = error.after
      ? { after: error.after }
      : error.before
        ? { before: error.before }
        : null;
    form.setError(error.field as never, {
      type: 'schema',
      message: error.code,
      ...(ref ? { types: ref } : {}),
    });
  }
}

const within = (path: string, parent: string) => path === parent || path.startsWith(`${parent}.`);

/**
 * Değişen alan yüzünden bayatlayan hatalar: alanın kendisi (ya da altı/üstü) ve ona göre sıralanan
 * zamanlar. Ör. Pilot Off ya da Cast Off değişince Pilot Off'un sıra hatası kalkar.
 */
export function staleErrorPaths(changed: string, errors: readonly FieldError[]): string[] {
  return errors
    .filter(
      (error) =>
        within(error.field, changed) ||
        within(changed, error.field) ||
        (error.after !== undefined && within(error.after, changed)) ||
        (error.before !== undefined && within(error.before, changed)),
    )
    .map((error) => error.field);
}

/**
 * Formun şema hatalarını yönetir: `apply` yazar, `clear` siler; kullanıcı bir alanı değiştirince
 * o alanın ve ona bağlı sıra hatası kendiliğinden kalkar (bir sonraki kayıtta yeniden denetlenir).
 */
export function useSchemaErrors<T extends FieldValues>(form: UseFormReturn<T>) {
  const applied = useRef<FieldError[]>([]);
  useEffect(() => {
    const subscription = form.watch((_values, { name }) => {
      if (!name || applied.current.length === 0) return;
      const stale = staleErrorPaths(name, applied.current);
      if (stale.length === 0) return;
      applied.current = applied.current.filter((error) => !stale.includes(error.field));
      form.clearErrors(stale as never);
    });
    return () => subscription.unsubscribe();
  }, [form]);
  const apply = useCallback(
    (errors: readonly FieldError[]) => {
      applied.current = [...errors];
      applyFieldErrors(form, errors);
    },
    [form],
  );
  const clear = useCallback(() => {
    applied.current = [];
    form.clearErrors();
  }, [form]);
  return { apply, clear };
}

/** Karşılaştırılan zamanın ekrandaki adı (`voyage.errors.timeRefs.<anahtar>`). */
export function timeRefKey(path: string, kind: unknown): string {
  // Kalkışın olay zamanı Cast Off (ADR 0058 §1)
  if (path === 'reportedAt') return kind === 'departure' ? 'castOff' : 'reportedAt';
  return path.split('.').at(-1) ?? path;
}

export interface ErrorSection {
  title: string;
  count: number;
}

/** Bölüm adlarıyla sayım: aynı bölüm bir kez, ilk görüldüğü sırada. */
export function groupSections(titles: readonly string[]): ErrorSection[] {
  const out: ErrorSection[] = [];
  for (const title of titles) {
    const found = out.find((section) => section.title === title);
    if (found) found.count += 1;
    else out.push({ title, count: 1 });
  }
  return out;
}

/** Özet metni: "Pilot, Cast Off & Tugs (1), Final Check (2)"; ekranda görünmeyenler ayrıca. */
export function sectionList(sections: readonly ErrorSection[], hidden: number, other: string) {
  const parts = sections.map((section) => `${section.title} (${section.count})`);
  if (hidden > 0) parts.push(`${other} (${hidden})`);
  return parts.join(', ');
}

/** Hata metni öğeleri (Mantine giriş sarmalayıcısı ve işaret kutusu). */
const ERROR_SELECTOR = '.mantine-InputWrapper-error, .mantine-Checkbox-error';

/** Alanın bölüm adı: içinde olduğu grup (`fieldset > legend`) ya da adlı bölge. */
function sectionTitle(element: Element): string | null {
  const legend = element.closest('fieldset')?.querySelector(':scope > legend');
  const text = legend?.textContent?.trim();
  if (text) return text;
  return element.closest('section[aria-label]')?.getAttribute('aria-label') ?? null;
}

/**
 * Ekrandaki hata metinlerini bölümlere göre sayar, ilk hatalı alanı görünür yapıp odaklar.
 * Dönen `shown`: ekranda görünen hata sayısı (gizli sekmedekiler ya da görünmeyen alanlar hariç).
 */
export function revealErrors(
  root: ParentNode | null,
  fallback: string,
): { sections: ErrorSection[]; shown: number } {
  if (!root) return { sections: [], shown: 0 };
  const elements = [...root.querySelectorAll(ERROR_SELECTOR)].filter(
    (element) => (element.textContent ?? '').trim() !== '',
  );
  const first = elements[0];
  if (first) {
    const id = first.id.endsWith('-error') ? first.id.slice(0, -'-error'.length) : '';
    const target =
      (id ? (root as Document | Element).querySelector?.(`[id="${id}"]`) : null) ??
      first.closest('fieldset')?.querySelector('input, textarea, button') ??
      null;
    (target ?? first).scrollIntoView?.({ block: 'center' });
    if (target instanceof HTMLElement) target.focus({ preventScroll: true });
  }
  return {
    sections: groupSections(elements.map((element) => sectionTitle(element) ?? fallback)),
    shown: elements.length,
  };
}
